import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import axiosInstance from '../services/axiosInstance';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { BuildingIcon, WalletIcon, PaperclipIcon, XIcon, Loader2Icon, FileTextIcon } from 'lucide-react';
import AttachmentPreviewCard from './AttachmentPreviewCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Solo Harcama Düzenleme Modalı — tüm alanları tek modalda düzenlenir
// (Tip, Tarih, Firma, Açıklama, Tutar, Para Birimi, Kategori, Proje).
// SoloExpenseEntry ile aynı iş kurallarını takip eder ama modal içinde.
const SoloExpenseEditModal = ({ expense, onClose, onSaved }) => {
  const [form, setForm] = useState({
    expense_type: expense?.expense_type || 'company',
    date: expense?.date || new Date().toISOString().slice(0, 10),
    supplier: expense?.supplier || '',
    description: expense?.description || '',
    category: expense?.category || '',
    amount: expense?.amount ?? '',
    currency: expense?.currency || 'TRY',
    cost_center_type: expense?.cost_center_type || 'general_expense',
    cost_center_id: expense?.cost_center_id || '',
    activity_id: expense?.activity_id || '',
    payment_method: expense?.payment_method || 'cash',
    credit_card_id: expense?.credit_card_id || '',
    attached_file: expense?.attached_file || null,
    subcategory: expense?.subcategory || null,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [billTypes, setBillTypes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [activities, setActivities] = useState([]);
  const [creditCards, setCreditCards] = useState([]);

  useEffect(() => {
    Promise.all([
      axiosInstance.get('/cost-centers/expense-types').catch(() => ({ data: [] })),
      axiosInstance.get('/bill-types').catch(() => ({ data: {} })),
      axiosInstance.get('/cost-centers/projects').catch(() => ({ data: [] })),
      axiosInstance.get('/cost-centers/activities').catch(() => ({ data: [] })),
      axiosInstance.get('/credit-cards').catch(() => ({ data: [] })),
    ]).then(([et, bt, pr, ac, cc]) => {
      setExpenseTypes(et.data?.expense_types || et.data || []);
      setBillTypes(bt.data?.bill_types || []);
      setProjects(pr.data?.projects || pr.data || []);
      setActivities(ac.data?.activities || ac.data || []);
      setCreditCards(Array.isArray(cc.data) ? cc.data : []);
    });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) {
      toast.error('Tutar 0\'dan büyük olmalıdır');
      return;
    }
    if (form.payment_method === 'credit_card' && !form.credit_card_id) {
      toast.error('Lütfen bir kredi kartı seçin');
      return;
    }
    setSaving(true);
    try {
      await axiosInstance.put(`/solo/expenses/${expense.id}`, {
        ...form,
        amount: Number(form.amount),
      });
      toast.success('Harcama güncellendi');
      onSaved && onSaved();
      onClose && onClose();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Güncelleme başarısız';
      toast.error(typeof msg === 'string' ? msg : 'Güncelleme başarısız');
    } finally {
      setSaving(false);
    }
  };

  // Belge yükleme — SoloExpenseEntry ile aynı endpoint. Modal içinde
  // dosya değiştirildiğinde form state'i güncellenir; Kaydet butonu
  // basıldığında yeni `attached_file` PUT ile kaydedilir.
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Dosya boyutu 10MB\'ı geçemez');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const token = localStorage.getItem('access_token');
      const fd = new FormData();
      fd.append('file', file);
      const resp = await axios.post(`${BACKEND_URL}/api/upload-expense-file`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      setForm((prev) => ({ ...prev, attached_file: resp.data }));
      toast.success('Belge yüklendi — kaydetmeyi unutmayın');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Belge yüklenemedi';
      toast.error(typeof msg === 'string' ? msg : 'Belge yüklenemedi');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeAttachedFile = () => {
    setForm((prev) => ({ ...prev, attached_file: null }));
  };

  if (!expense) return null;

  return (
    <Dialog open={!!expense} onOpenChange={(o) => { if (!o) onClose && onClose(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="solo-expense-edit-modal">
        <DialogHeader>
          <DialogTitle>Harcamayı Düzenle</DialogTitle>
          <p className="text-xs text-gray-500 mt-0.5">
            Tüm alanları güncelleyebilirsiniz. Kaydettiğinizde değişiklik anında yansır.
          </p>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 py-2">
          {/* Tip */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Tip *</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: 'company', label: 'Şirket Harcaması', Icon: BuildingIcon, color: 'blue' },
                { id: 'personal', label: 'Şahsi Harcama', Icon: WalletIcon, color: 'emerald' },
              ].map(({ id, label, Icon, color }) => {
                const active = form.expense_type === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setForm({ ...form, expense_type: id })}
                    className={`flex items-center gap-2 p-2.5 rounded-lg border-2 text-sm ${
                      active
                        ? (color === 'emerald' ? 'border-emerald-500 bg-emerald-50 text-emerald-900' : 'border-blue-500 bg-blue-50 text-blue-900')
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                    }`}
                    data-testid={`edit-type-${id}`}
                  >
                    <Icon className="w-4 h-4" /> {label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tarih *</label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="edit-date" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Firma / Yer</label>
              <Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} placeholder="Örn: Migros" data-testid="edit-supplier" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama</label>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="edit-description" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tutar *</label>
              <Input type="number" step="0.01" min="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required data-testid="edit-amount" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Para Birimi</label>
              <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}
                className="w-full px-3 py-2 border rounded-md text-sm" data-testid="edit-currency">
                <option value="TRY">TRY ₺</option><option value="USD">USD $</option>
                <option value="EUR">EUR €</option><option value="GBP">GBP £</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kategori</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full px-3 py-2 border rounded-md text-sm bg-white" data-testid="edit-category">
                <option value="">— Seçiniz —</option>
                {expenseTypes.length > 0 && (
                  <optgroup label="Masraf Merkezleri">
                    {expenseTypes.map((t) => (
                      <option key={t.id || t.expense_type_name} value={t.expense_type_name}>{t.expense_type_name}</option>
                    ))}
                  </optgroup>
                )}
                {billTypes.length > 0 && (
                  <optgroup label="Faturalar">
                    {billTypes.map((b) => {
                      const label = b.custom_label ? `${b.parent_label_tr} — ${b.custom_label}` : b.parent_label_tr;
                      return (<option key={b.id} value={`Fatura: ${label}`}>{label}</option>);
                    })}
                  </optgroup>
                )}
                {/* Fallback for legacy/free-text categories not in the loaded lists */}
                {form.category && ![
                  ...expenseTypes.map(t => t.expense_type_name),
                  ...billTypes.map(b => b.custom_label ? `Fatura: ${b.parent_label_tr} — ${b.custom_label}` : `Fatura: ${b.parent_label_tr}`)
                ].includes(form.category) && (
                  <option value={form.category}>{form.category} (mevcut)</option>
                )}
              </select>
            </div>
            {form.expense_type === 'company' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Proje (Opsiyonel)</label>
                <select
                  value={form.cost_center_id}
                  onChange={(e) => setForm({
                    ...form,
                    cost_center_id: e.target.value,
                    cost_center_type: e.target.value ? 'project' : 'general_expense',
                  })}
                  className="w-full px-3 py-2 border rounded-md text-sm bg-white"
                  data-testid="edit-project"
                >
                  <option value="">— Proje yok (Genel Gider) —</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.project_name || p.name}</option>
                  ))}
                </select>
              </div>
            )}
            {form.expense_type === 'personal' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Aktivite (Opsiyonel)</label>
                <select
                  value={form.activity_id}
                  onChange={(e) => setForm({ ...form, activity_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md text-sm bg-white"
                  data-testid="edit-activity"
                >
                  <option value="">— Aktivite yok —</option>
                  {activities.map((a) => (
                    <option key={a.id} value={a.id}>{a.activity_name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Ödeme Türü — Nakit / Kredi Kartı. Kart seçilirse aşağıda kart
              dropdown'u çıkar. */}
          <div className="grid grid-cols-2 gap-3" data-testid="edit-payment-block">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ödeme Türü *</label>
              <select
                value={form.payment_method}
                onChange={(e) => setForm({
                  ...form,
                  payment_method: e.target.value,
                  credit_card_id: e.target.value === 'cash' ? '' : form.credit_card_id,
                })}
                className="w-full px-3 py-2 border rounded-md text-sm bg-white"
                data-testid="edit-payment-method"
              >
                <option value="cash">💵 Nakit</option>
                <option value="credit_card">💳 Kredi Kartı</option>
              </select>
            </div>
            {form.payment_method === 'credit_card' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kart *</label>
                <select
                  value={form.credit_card_id}
                  onChange={(e) => setForm({ ...form, credit_card_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md text-sm bg-white"
                  data-testid="edit-credit-card"
                >
                  <option value="">— Kart seçiniz —</option>
                  {creditCards.map((c) => (
                    <option key={c.id} value={c.id}>{c.card_name} •{c.last4}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Belge — ekli dosya varsa gösterir + kaldırma; yoksa yükleme
              alanı. Belgesiz kaydedilmiş harcamalar için sonradan belge
              ekleme akışı burada. */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Belge (opsiyonel)</label>
            {form.attached_file ? (
              <AttachmentPreviewCard
                file={form.attached_file}
                onRemove={removeAttachedFile}
                testIdPrefix="edit-file"
              />
            ) : (
              <label
                className={`flex items-center justify-center gap-2 p-3 rounded-md border-2 border-dashed cursor-pointer text-sm transition ${
                  uploading
                    ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                    : 'border-gray-300 bg-white text-gray-600 hover:border-teal-400 hover:text-teal-700 hover:bg-teal-50'
                }`}
                data-testid="edit-file-upload-label"
              >
                {uploading ? (
                  <>
                    <Loader2Icon className="w-4 h-4 animate-spin" />
                    Yükleniyor...
                  </>
                ) : (
                  <>
                    <PaperclipIcon className="w-4 h-4" />
                    Belge Ekle (fiş / fatura fotoğrafı) — JPG/PNG/PDF, maks. 10 MB
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleFileChange}
                  className="hidden"
                  disabled={uploading}
                  data-testid="edit-file-input"
                />
              </label>
            )}
          </div>

          <DialogFooter className="pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving || uploading}>
              İptal
            </Button>
            <Button type="submit" disabled={saving || uploading} data-testid="edit-save-btn">
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SoloExpenseEditModal;
